import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import api from "../../services/api";

const ProfileCard = ({ profile, onProfileChange, onError }) => {
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState(profile.displayName || "");
  const [editingSchoolHashtag, setEditingSchoolHashtag] = useState(false);
  const [newSchoolHashtag, setNewSchoolHashtag] = useState(profile.schoolHashtag || "");
  const [busy, setBusy] = useState(false);

  // --- NEW: IG quota state ---
  const [igQuota, setIgQuota] = useState({
    usage: null,
    total: null,
    remaining: null,
    loading: false,
    error: null,
  });

  const isIGConnected = profile.instagramUsername && profile.instagramUsername !== "Not connected";

  const saveDisplayName = async () => {
    if (!newDisplayName.trim()) return onError?.("Display name cannot be empty.");
    setBusy(true);
    try {
      await api.post("/auth/update-display-name", { new_display_name: newDisplayName });
      onProfileChange?.({ ...profile, displayName: newDisplayName });
      setEditingDisplayName(false);
    } catch (e) {
      onError?.(e.response?.data?.detail || "Failed to update display name.");
    } finally {
      setBusy(false);
    }
  };

  const saveHashtag = async () => {
    if (!newSchoolHashtag.trim()) return onError?.("School hashtag cannot be empty.");
    setBusy(true);
    try {
      await api.post("/auth/update-school-hashtag", { new_hashtag: newSchoolHashtag });
      onProfileChange?.({ ...profile, schoolHashtag: newSchoolHashtag });
      setEditingSchoolHashtag(false);
    } catch (e) {
      onError?.(e.response?.data?.detail || "Failed to update school hashtag.");
    } finally {
      setBusy(false);
    }
  };

  const connectInstagram = async () => {
    try {
      const { data } = await api.get("/auth/instagram-login");
      if (data.url) window.location.href = data.url;
    } catch (e) {
      onError?.(e.response?.data?.detail || "Could not connect to Instagram. Please try again.");
    }
  };

  // --- NEW: load IG quota when connected ---
  const loadQuota = async () => {
    if (!isIGConnected) {
      setIgQuota({ usage: null, total: null, remaining: null, loading: false, error: null });
      return;
    }
    setIgQuota((q) => ({ ...q, loading: true, error: null }));
    try {
      const { data } = await api.get("/auth/instagram/content-publishing-limit");
      setIgQuota({
        usage: data.quota_usage,
        total: data.quota_total,
        remaining: data.remaining,
        loading: false,
        error: null,
      });
    } catch (e) {
      setIgQuota((q) => ({
        ...q,
        loading: false,
        error:
          e?.response?.data?.detail ||
          "Could not read Instagram quota right now.",
      }));
    }
  };

  useEffect(() => {
    loadQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIGConnected]);

  return (
    <div className="profile-content">
      <p>
        <strong>Email:</strong>{" "}
        <span className="profile-value">{profile.email}</span>
      </p>

      <p>
        <strong>Display Name:</strong>{" "}
        {editingDisplayName ? (
          <>
            <input
              type="text"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              maxLength={255}
              placeholder="e.g. Ms. Smith or Mr. Jones"
              className="displayname-input"
              style={{ marginRight: 10 }}
            />
            <button type="button" onClick={saveDisplayName} disabled={busy}>
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingDisplayName(false);
                setNewDisplayName(profile.displayName);
              }}
              style={{ marginLeft: 5 }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="profile-value">{profile.displayName}</span>{" "}
            {profile.displayName === profile.email.split("@")[0] ? (
              <button
                type="button"
                onClick={() => setEditingDisplayName(true)}
                style={{
                  fontSize: 12,
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                Click to Change your Display Name
                <span className="nudge-arrow" title="Click here to personalize your display name!">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setEditingDisplayName(true)}
                style={{ fontSize: 12, backgroundColor: "#00FFAF" }}
              >
                Edit
              </button>
            )}
          </>
        )}
      </p>

      <p>
        <strong>School:</strong>{" "}
        <span className="profile-value">{profile.schoolName}</span>
      </p>

      <p>
        <strong>School Hashtag:</strong>{" "}
        {editingSchoolHashtag ? (
          <>
            <input
              type="text"
              value={newSchoolHashtag}
              onChange={(e) => setNewSchoolHashtag(e.target.value)}
              maxLength={50}
              placeholder="e.g. #OurSchoolName"
              className="hashtag-input"
              style={{ marginRight: 10 }}
            />
            <button
              type="button"
              onClick={saveHashtag}
              disabled={busy}
              style={{ backgroundColor: "#00FFAF", color: "white", marginRight: 10 }}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingSchoolHashtag(false);
                setNewSchoolHashtag(profile.schoolHashtag || "");
              }}
              style={{ backgroundColor: "#00FFAF", color: "white", marginLeft: 5 }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="profile-value">{profile.schoolHashtag || "Not set"}</span>{" "}
            <button
              type="button"
              onClick={() => setEditingSchoolHashtag(true)}
              style={{
                fontSize: 12,
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: "#00FFAF",
                color: "white",
              }}
            >
              {profile.schoolHashtag ? "Edit" : "Click to set your School Hashtag"}
              {!profile.schoolHashtag && (
                <span className="nudge-arrow" title="Click here to set your hashtag!">
                  <FontAwesomeIcon icon={faArrowLeft} />
                </span>
              )}
            </button>
          </>
        )}
      </p>

      <p>
        <strong>Total Photos:</strong>{" "}
        <span className="profile-value">{profile.totalPhotos}</span>
      </p>

      <p>
        <strong>Instagram Account:</strong>{" "}
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <span className="profile-value">
            {profile.instagramUsername === "Not connected"
              ? profile.instagramUsername
              : `@${profile.instagramUsername}`}
          </span>
          {profile.instagramUsername === "Not connected" && (
            <button
              type="button"
              className="instagram-connect-button"
              onClick={connectInstagram}
              style={{ fontSize: 12, marginLeft: 10 }}
            >
              Connect to Instagram
            </button>
          )}
        </span>
      </p>

      {/* --- NEW: Instagram Publishing Quota panel (visible when connected) --- */}
      {isIGConnected && (
        <div
          style={{
            background: "#f8f9fb",
            border: "1px solid #e4e7ec",
            padding: "10px 12px",
            borderRadius: 8,
            margin: "10px 0 16px",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <strong>Instagram Publishing Quota</strong>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              {igQuota.loading && <span>Checking quota…</span>}
              {!igQuota.loading &&
                typeof igQuota.usage === "number" &&
                typeof igQuota.total === "number" &&
                typeof igQuota.remaining === "number" && (
                <span>
                  Used <strong>{igQuota.usage}</strong> of <strong>{igQuota.total}</strong> in the last 24h
                  &nbsp;|&nbsp; Remaining: <strong>{igQuota.remaining}</strong>
                </span>
              )}
              {!igQuota.loading &&
                (igQuota.usage === null || igQuota.total === null || igQuota.remaining === null) && (
                <span style={{ opacity: 0.8 }}>
                  {igQuota.error
                    ? igQuota.error
                    : "Quota is unavailable right now."}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={loadQuota}
            disabled={igQuota.loading}
            style={{ fontSize: 12 }}
          >
            {igQuota.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      )}

      <p>
        <strong>School ID:</strong>{" "}
        <span className="profile-value">{profile.schoolId || "No school Id assigned"}</span>
      </p>
    </div>
  );
};

export default ProfileCard;
